import {EventClickArg, EventInput} from '@fullcalendar/core/index.js';
import FullCalendar from '@fullcalendar/react';
import {addDays, differenceInDays, format, setHours} from 'date-fns';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useResize} from 'app/lib/hooks/useResize';
import {TEvent} from 'entities/calendar';
import {ru} from 'date-fns/locale';
import {NewEventFormValues} from 'shared/types';
import {utcToUserDate} from 'features/calendar/helpers/dates.ts';

type useCalendarCalendarProps = {
  onChangeFilters: (filters: {date?: Date; hiveIds?: string[]}) => void;
  activeDate: Date;
  events: TEvent[];
};

export const useCalendarCalendar = ({
  onChangeFilters,
  activeDate,
  events,
}: useCalendarCalendarProps) => {
  const {isScreenLg} = useResize();

  const calendarRef = useRef<FullCalendar | null>(null);

  const getApi = useCallback(() => calendarRef.current?.getApi(), []);

  const [detailEvent, setDetailEvent] = useState<TEvent | null>(null);
  const [isdetailOpen, setIsDetailOpen] = useState<boolean>(false);

  const [editId, setEditId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<NewEventFormValues> | undefined>();
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  const touchStartPoint = useRef<number>(0);
  const touchEndPoint = useRef<number>(0);

  const weekRange = ['timeGridWeek', 'listWeek', 'listNext7'];
  const monthRange = ['dayGridMonth', 'listMonth'];

  const onPrevClick = (viewTypeArg?: string) => {
    const viewType = viewTypeArg || getApi()?.view?.type || '';

    const days = weekRange.includes(viewType) ? -7 : monthRange.includes(viewType) ? -30 : -1;

    onChangeFilters({date: addDays(activeDate, days)});
  };

  const onNextClick = (viewTypeArg?: string) => {
    const viewType = viewTypeArg || getApi()?.view?.type || '';

    const days = weekRange.includes(viewType) ? 7 : monthRange.includes(viewType) ? 30 : 1;

    onChangeFilters({date: addDays(activeDate, days)});
  };

  const onViewChange = (value: string | number) => {
    const api = getApi();
    if (!api) return;
    api.changeView(String(value));
  };

  const refactorEventCallback = useCallback((event: TEvent): EventInput[] => {
    const descr = event.description;
    const start = utcToUserDate(event.startAt);
    const end = utcToUserDate(event.endAt);
    const id = event.id;

    const diff = differenceInDays(end, start);

    if (diff > 0) {
      const out: EventInput[] = [];
      for (let index = 0; index < diff + 1; index++) {
        const item: EventInput = {
          title: `с ${format(start, 'dd LLL', {locale: ru})} ${descr} до ${format(end, 'dd LLL', {locale: ru})}`,
          editable: false,
          extendedProps: {id},
        };

        if (index < diff) item.allDay = true;
        else item.end = end;

        item.start = index < 1 ? start : setHours(addDays(start, index), 0);

        out.push(item);
      }
      return out;
    }

    return [
      {
        title: descr,
        start,
        end,
        extendedProps: {id},
      },
    ];
  }, []);

  const eventItems = useMemo(() => {
    return events.map((el) => refactorEventCallback(el)).flat();
  }, [events, refactorEventCallback]);

  const onCalendarEventClick = (arg: EventClickArg) => {
    const id = arg?.event?.extendedProps?.id;
    if (!id) return;

    const evt = events.find((el) => el.id === id || String(el.id) === String(id));
    if (!evt) return;

    setDetailEvent(evt);
    setTimeout(() => setIsDetailOpen(true), 300);
  };

  const onEditClick = (event: TEvent) => {
    setIsDetailOpen(false);
    setEditId(event.id);
    setEditValues({
      hiveIds: event.hives.map((el) => `${el.id}`),
      hives: event.hives
        .map((el) => ({key: el.id, value: `№${el.number}`}))
        .filter((el) => el && el.key && el.value),
      description: event.description,
      startAt: utcToUserDate(event.startAt),
      endAt: utcToUserDate(event.endAt),
      notifyAt: event.notifyAt ? utcToUserDate(event.notifyAt) : undefined,
      notifyAtRepeat: event.notifyAtRepeat ? utcToUserDate(event.notifyAtRepeat) : undefined,
    });

    setTimeout(() => setIsEditOpen(true), 200);
  };

  const checkSwipe = () => {
    if (!touchStartPoint.current || !touchEndPoint.current) return;

    const delta = touchStartPoint.current - touchEndPoint.current;

    if (delta < -100) onPrevClick('timeGridWeek');
    if (delta > 100) onNextClick('timeGridWeek');

    touchStartPoint.current = 0;
    touchEndPoint.current = 0;
  };

  const onCalendarTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartPoint.current = event.changedTouches[0].screenX;
  };

  const onCalendarTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    touchEndPoint.current = event.changedTouches[0].screenX;
    setTimeout(checkSwipe, 10);
  };

  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const api = getApi();
        if (!api) return;
        api.gotoDate(activeDate);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeDate]);

  return {
    eventItems,
    isScreenLg,
    calendarRef,
    detailEvent,
    isdetailOpen,
    editId,
    editValues,
    isEditOpen,
    onPrevClick,
    onNextClick,
    onViewChange,
    setIsDetailOpen,
    onCalendarEventClick,
    onEditClick,
    setIsEditOpen,
    setEditId,
    onCalendarTouchStart,
    onCalendarTouchEnd,
    setDetailEvent,
    getApi,
  };
};
